'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  isSafeRedirectPath,
  peekPostLoginRedirect,
  signInWithReturn,
} from '@/lib/auth-redirect';
import { RecruitingPrograms } from '@/components/landing/recruiting-programs';
import { HelpButton } from '@/components/help/help-button';

/**
 * 서비스 소개 문구. 조직 설치형이라 기관별로 달라질 수 있어 상수로 분리해 둔다
 * (추후 org 설정으로 이관 가능). 계획: docs/plans/2026-08-18/게스트-신청-에디터-화면.md
 */
// 줄이 바뀐다면 뜻 덩이 사이에서만 바뀐다. 덩이 하나는 inline-block 이라 좁은 화면에서 「스템」만 내려가지 않는다(10-04 사용자).
const SERVICE_TAGLINE = ['AI 에이전트 기반', 'SW중심대학 관리 시스템'];
const SERVICE_SUBTITLE = ['프로그램 신청부터', '서류 작성·제출까지', 'AI가 함께합니다.'];

function Phrases({ parts }: { parts: string[] }) {
  return parts.map((part, i) => (
    <Fragment key={part}>
      {i > 0 ? ' ' : null}
      <span className="inline-block break-keep">{part}</span>
    </Fragment>
  ));
}

interface LandingPageProps {
  error?: string;
  callbackUrl?: string;
}

export function LandingPage({ error, callbackUrl }: LandingPageProps) {
  const { toast } = useToast();
  const [signingIn, setSigningIn] = useState<'login' | 'register' | null>(null);

  useEffect(() => {
    if (error) {
      console.warn('[LandingPage] OAuth error received:', error);
      toast({
        variant: 'destructive',
        title: '로그인 실패',
        description:
          '로그인 처리 중 문제가 발생했습니다. 로그인 버튼을 다시 눌러 주세요.',
      });
      // URL에서 error 파라미터 제거 (뒤로가기 시 재표시 방지)
      window.history.replaceState({}, '', '/');
    }
  }, [error, toast]);
  // 보호된 주소로 왔다가 튕겨 온 사람(proxy 가 callbackUrl 을 붙인다)은 그 주소를 열려고 온 것이다 - 신청자용
  // 첫 화면에서 「로그인」을 한 번 더 찾게 하지 않고 바로 로그인으로 보낸다(2026-09-24 실측: 선발 로그인 안내 메일의
  // 「로그인하고 프로그램 열기」가 이 화면에 멈췄다). 로그인이 실패해 돌아왔으면(error) 보내지 않는다. 로그인 화면에서
  // 뒤로 돌아온 사람이 다시 튕겨 나가지 않게 같은 주소는 2분에 한 번만 보낸다.
  const autoSignInTried = useRef(false);
  useEffect(() => {
    if (autoSignInTried.current || error || !isSafeRedirectPath(callbackUrl)) return;
    autoSignInTried.current = true;
    if (recentlyAutoSignedIn(callbackUrl)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSigningIn('login');
    signInWithReturn('keycloak', callbackUrl);
  }, [callbackUrl, error]);

  function handleSignIn(provider: 'keycloak' | 'keycloak-register') {
    setSigningIn(provider === 'keycloak' ? 'login' : 'register');
    // peekPostLoginRedirect: 사용자가 protected URL 에서 미인증으로 리다이렉트되어
    // 랜딩에 도달한 케이스에서, 원래 가려던 path 를 callbackUrl 로 사용.
    // sessionStorage 키는 AuthProvider/PostLoginGate 의 인증 완료 transition 에서
    // 최종 consume 되므로 여기서는 peek (signIn 실패 시 키 보존).
    const target = isSafeRedirectPath(callbackUrl)
      ? callbackUrl
      : (peekPostLoginRedirect() ?? '/dashboard');
    signInWithReturn(provider, target);
  }

  return (
    <main className="min-h-screen bg-background">
      {/* 상단 헤더: 로그인은 항상 여기 있다 (발급받은 계정·담당자·관리자 진입로) */}
      <header className="border-b">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between px-4 py-3">
          <span className="text-lg font-bold tracking-tight">한채움AI</span>
          {/* 한 화면의 주인공은 하나다 (2026-09-20). 브랜드색으로 꽉 찬 단추는 「신청하기」가
              가져간다 - 처음 오는 사람이 할 일은 신청이지 로그인이 아니다. 로그인은 늘 있어야
              하지만 외칠 자리는 아니라서 테두리 단추로 둔다. 사라지지도 흐려지지도 않는다. */}
          <div className="flex items-center gap-2">
            <HelpButton pathname="/" iconOnly />
            <Button
              variant="outline"
              size="sm"
              disabled={signingIn !== null}
              onClick={() => handleSignIn('keycloak')}
            >
              {signingIn === 'login' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              로그인
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-4xl space-y-10 px-4 py-10">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl"><Phrases parts={SERVICE_TAGLINE} /></h1>
          <p className="text-muted-foreground"><Phrases parts={SERVICE_SUBTITLE} /></p>
        </div>

        <ServiceIntro />

        <RecruitingPrograms />

      </div>

      {/* 로그인 없이 보는 처리방침·약관(사람 10-06 「첫 페이지 하단에 링크」, 법 §30② 공개) */}
      <footer className="border-t">
        <div className="mx-auto w-full max-w-4xl space-y-2 px-4 py-6 text-xs text-muted-foreground">
          {/* 사람 10-06: 시험 운영이고 2027년 2월까지 보관한 뒤 모든 자료를 파기한다는 것을 밝혀 둔다(처리방침·약관 머리와 같은 말) */}
          <p>
            이 서비스는 전북대학교 SW중심대학사업단의 2026년 2학기 일부 프로그램을 위한 시험 운영입니다.
            자료는 2027년 2월까지 보관하고, 그 뒤 모든 자료를 파기합니다.
          </p>
          <nav aria-label="약관과 방침" className="flex flex-wrap gap-x-4 gap-y-1">
            <Link href="/privacy" className="font-medium text-foreground/80 hover:text-foreground">개인정보 처리방침</Link>
            <Link href="/terms" className="hover:text-foreground">이용약관</Link>
            <span>(주)채움에이아이</span>
          </nav>
        </div>
      </footer>
    </main>
  );
}

const AUTO_SIGN_IN_KEY = 'hai.landing.autoSignIn';
const AUTO_SIGN_IN_WINDOW_MS = 2 * 60 * 1000;

/** 같은 주소로 방금 자동 로그인을 보냈으면 true. 저장소를 못 쓰면 매번 보낸다(되돌이 고리보다 한 번 더 누르는 게 낫지 않다). */
function recentlyAutoSignedIn(path: string): boolean {
  try {
    const raw = window.sessionStorage.getItem(AUTO_SIGN_IN_KEY);
    const last = raw ? (JSON.parse(raw) as { path?: string; at?: number }) : null;
    const now = Date.now();
    if (last?.path === path && typeof last.at === 'number' && now - last.at < AUTO_SIGN_IN_WINDOW_MS) {
      return true;
    }
    window.sessionStorage.setItem(AUTO_SIGN_IN_KEY, JSON.stringify({ path, at: now }));
  } catch {
    // 저장소를 못 쓰면 기록 없이 보낸다.
  }
  return false;
}

const INTRO_SEEN_KEY = 'hai.landing.intro.seen';

/**
 * 처음 오는 학생을 위한 서비스 소개. 담당 선생님 요청(2026-08-25). 팝업이 아니라 신청 카드
 * 바로 위에 항상 있는 블록이다: 팝업은 닫으면 다시 못 보고 폰에서 화면을 가린다. 첫 방문에는
 * 펼쳐진 채로, 그 뒤로는 접힌 채로 보이며 언제든 다시 펼 수 있다.
 */
function ServiceIntro() {
  // 첫 그리기는 서버와 같아야 하므로 접힘으로 시작하고, 마운트 뒤 저장값을 보고 펼친다.
  const [open, setOpen] = useState(false);
  // 저장값은 브라우저에만 있어 마운트 뒤에야 읽을 수 있다. 첫 그리기(SSR)와 어긋나지 않게 effect 에서 연다.
  useEffect(() => {
    let firstVisit = true;
    try {
      firstVisit = window.localStorage.getItem(INTRO_SEEN_KEY) !== '1';
      if (firstVisit) window.localStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch {
      // 저장소를 못 쓰면 매번 첫 방문으로 본다.
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (firstVisit) setOpen(true);
  }, []);

  return (
    <section aria-labelledby="service-intro-title" className="rounded-lg border bg-muted/30 text-sm">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
        aria-controls="service-intro-body"
        onClick={() => setOpen((value) => !value)}
      >
        <span id="service-intro-title" className="font-semibold">처음이신가요?</span>
        <span className="text-xs text-muted-foreground">{open ? '접기' : '펼치기'}</span>
      </button>
      {open && (
        <div id="service-intro-body" className="space-y-3 border-t px-4 py-4">
          <ol className="list-decimal space-y-1.5 pl-5">
            <li>
              아래에서 <span className="font-medium">지원할 프로그램</span>을 고릅니다.
            </li>
            <li>
              이름과 이메일을 입력하면 <span className="font-medium">계정이 자동으로 만들어지고</span>{' '}
              작성 주소가 메일로 갑니다. (미수신 시, 스팸함 확인)
            </li>
            <li>
              작성 화면에서 「AI 초안 만들기」를 누르면 AI 가 초안을 제안합니다(30초 가량 소요).
              승인한 것만 문서에 들어가고, 직접 웹에디터에서 고칠 수도 있습니다.
            </li>
            <li>
              PDF 변환 후, 그림/서명을 추가한 다음, <span className="font-medium">최종 제출하시면,</span> 담당자가 검토한 뒤 안내드립니다.
            </li>
          </ol>
          {/* 종전에는 화면 맨 아래 따로 선 칸이었다(2026-09-20 여기로 옮김). 「처음이신가요」가
              묻는 것과 같은 이야기라 두 곳에 있을 이유가 없었다. 베타 안내의 뒷문장
              (「신청서를 시작하면 계정이 자동으로…」)은 위 2번과 같은 말이라 걷었다. */}
          <div className="space-y-1 border-t pt-3">
            <p>
              계정이 있으시면 오른쪽 위 <span className="font-medium">로그인</span>으로 참여 중인
              프로그램과 작성할 문서를 볼 수 있습니다.
            </p>
            <p className="text-muted-foreground">지금은 베타테스트 기간입니다.</p>
          </div>
        </div>
      )}
    </section>
  );
}
